import { useEffect, useState } from 'react'

import classNames from 'classnames'

import AnimateIntoView, {
  AnimationType,
} from '~/components/atoms/AnimateIntoView/AnimateIntoView'
import { ButtonVariants } from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import Ctas from '~/components/atoms/Ctas/Ctas'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Col, Grid, Row } from '~/components/atoms/Grid'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type { ThemeType } from '~/components/serializers'
import { ComponentTheme } from '~/components/themes'
import { FormTypes } from '~/data/forms/shared/interfaces/Form'
import { ButtonVariants as EnumButtonVariants } from '~/enums/buttonVariants'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { toURL } from '~/utils/urlResolver'

import styles from './SpeakToExpert.module.css'

export interface SpeakToExpertProps {
  testID?: string
  title: string
  subtitle?: string
  backgroundImage?: ImageProps
  titleTag?: string
  experts: {
    description: string
    expertArticleLink?: {
      title: string
      slug: string
      url: any
    }
    name: string
    jobTitle: string
    image: ImageProps
    slug: string
    email: string
  }[]
  moduleId?: string
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  theme: ComponentTheme
  useContainer?: boolean
  variant?: ThemeType
  ctas?: any
}

export default function SpeakToExpert({
  testID,
  title,
  subtitle,
  titleTag = 'h3',
  experts,
  moduleId,
  verticalPadding,
  noPaddingBottom,
  theme,
  backgroundImage,
  useContainer = false,
  variant,
  ctas,
}: SpeakToExpertProps) {
  const [currentExpert, setCurrentExpert] = useState<
    SpeakToExpertProps['experts'][0]
  >(experts[0])

  const [sortedExperts, setSortedExperts] =
    useState<SpeakToExpertProps['experts']>()
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const primaryCta = ctas ? (
    <div className={styles.ctaWrapper}>
      <Ctas ctas={ctas} theme={ComponentTheme.secondary} />
    </div>
  ) : (
    <div className={styles.ctaWrapper}>
      <Ctas
        ctas={[
          {
            linkText: `Request an appraisal`,
            launchForm: FormTypes.masterpiece,
            buttonStyle: EnumButtonVariants.primary,
            variant: ButtonVariants.primaryDark,
            children: undefined,
          },
        ]}
        theme={ComponentTheme.secondary}
      />
      <Ctas
        hideIcon
        ctas={[
          {
            linkText: `The team`,
            children: ['The team'],
            link: `/our-people`,
            buttonStyle: EnumButtonVariants.secondary,
            variant: ButtonVariants.primaryDark,
          },
        ]}
        theme={ComponentTheme.primary}
      />
    </div>
  )
  useEffect(() => {
    setSortedExperts(experts.reverse())
  }, [])

  const componentThemes: any = {
    dark: {
      primary: 'primary',
      secondary: 'secondary',
      black: 'richBlack',
    },
    light: {
      primary: 'primary',
      secondary: 'secondary',
      black: 'black',
    },
  }

  const renderWrap = () => {
    return (
      <Fixture
        className={classNames(
          styles.base,
          backgroundImage && styles.withBackgroundImage
        )}
        data-testid={testID}
        id={moduleId}
        verticalPadding={verticalPadding}
        noPaddingBottom={noPaddingBottom}
        theme={variant ? componentThemes[variant][theme] : theme}
      >
        {backgroundImage && (
          <div className={styles.backgroundImageWrap}>
            <Image
              asset={backgroundImage?.asset}
              className={styles.backgroundImage}
              objectFit="cover"
              width={60}
            />
          </div>
        )}
        <AnimateIntoView type={AnimationType.fadeIn}>
          <div className={styles.contentContainer}>
            <Container>
              <Grid>
                <Row>
                  <Col variant="col-1/2--md">
                    <div className={styles.content}>
                      {title && (
                        <Text
                          size={TextSizes.primaryTitleLrgPlus}
                          Tag={titleTag as any}
                          className={styles.title}
                          dataNoSnippet={true}
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html: title,
                            }}
                          />
                        </Text>
                      )}
                      {subtitle && (
                        <Text
                          size={TextSizes.secondaryTitleLrg}
                          className={styles.subtitle}
                          dataNoSnippet={true}
                        >
                          {subtitle}{' '}
                        </Text>
                      )}
                      <Text
                        size={TextSizes.secondaryTitleLrg}
                        dataNoSnippet={true}
                      >
                        {currentExpert?.description}
                      </Text>
                      {!isMobile && primaryCta}
                    </div>
                  </Col>
                  <Col variant="col-1/2--md">
                    <div className={styles.sideContainer}>
                      <div className={styles.sideContent}>
                        <div className={styles.expertsContainer}>
                          {sortedExperts?.map((expert: any, key: number) => {
                            return (
                              <div
                                className={classNames(
                                  styles.avatar,
                                  currentExpert?.name !== expert.name &&
                                    styles.avatarDark,
                                  currentExpert?.name === expert.name &&
                                    styles.avatarActive
                                )}
                                key={key}
                                onClick={() => {
                                  setCurrentExpert(expert)
                                }}
                              >
                                <Image
                                  asset={expert?.image?.asset}
                                  alt={currentExpert?.name}
                                  width={60}
                                />
                              </div>
                            )
                          })}
                        </div>
                        <div className={styles.expertBio}>
                          <Text
                            Tag="p"
                            size={TextSizes.primaryTitleSml}
                            className={styles.expertTitle}
                          >
                            {currentExpert?.name}
                          </Text>
                          <Text
                            Tag="p"
                            size={TextSizes.secondaryTitleLrg}
                            className={styles.expertJobTitle}
                          >
                            {currentExpert?.jobTitle}
                          </Text>
                        </div>

                        {currentExpert?.expertArticleLink && (
                          <div className={styles.cta}>
                            {}
                            <Ctas
                              ctas={[
                                {
                                  linkText: `${currentExpert.expertArticleLink.title}`,
                                  children: [
                                    `${currentExpert.expertArticleLink.title}`,
                                  ],
                                  link: toURL(
                                    currentExpert.expertArticleLink.url
                                  ),
                                  buttonStyle: EnumButtonVariants.textLink,
                                  variant: ButtonVariants.secondaryDark,
                                },
                              ]}
                              theme={ComponentTheme.secondary}
                            />
                          </div>
                        )}
                      </div>
                      {isMobile && primaryCta}
                    </div>
                  </Col>
                </Row>
              </Grid>
            </Container>
          </div>
        </AnimateIntoView>
      </Fixture>
    )
  }

  return useContainer ? <Container>{renderWrap()}</Container> : renderWrap()
}
